import React from 'react';

import { useAppFormContext } from '@/app/lib/tanstack-form';

import { Button } from './button';

export type FormButtonProps = React.ComponentProps<typeof Button> & {
  /** Content to show when form is submitting. If not provided, uses children */
  submittingChildren?: React.ReactNode;
};

/**
 * A form button component that integrates with TanStack Form to automatically
 * handle disabled state during form submission.
 *
 * The button is automatically disabled when the form is submitting, and
 * optionally shows different content during submission.
 *
 * @example
 * Basic usage:
 * ```tsx
 * <FormButton type="submit">Save</FormButton>
 * ```
 *
 * @example
 * With custom submitting content:
 * ```tsx
 * <FormButton type="submit" submittingChildren="Saving...">
 *   Save Contact
 * </FormButton>
 * ```
 */
export function FormButton({
  submittingChildren,
  disabled,
  children,
  ...props
}: FormButtonProps) {
  const form = useAppFormContext();
  const isSubmitting = form.state.isSubmitting;

  return (
    <Button
      {...props}
      disabled={disabled || isSubmitting}
    >
      {isSubmitting && submittingChildren ? submittingChildren : children}
    </Button>
  );
}
